import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DOMAIN_VALIDATION_REGEX } from 'src/components/Forms';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { PartnerDomainFormFields, PartnerDomainFormModel } from './types';

export const AddPartnerDomainModal = () => {
  const theme = useTheme();
  const { closeModal } = useModalMethods(Modals.AddPartnerDomainModal);
  const { isOpened, modalProps } = useModalState(Modals.AddPartnerDomainModal);

  const { control, register, formState, getValues, setError, clearErrors, reset } =
    useForm<PartnerDomainFormModel>({
      defaultValues: { domain: '' },
    });

  const closeModalAndResetForm = useCallback(() => {
    reset();
    closeModal();
  }, [reset, closeModal]);

  const onFormSubmit = useCallback(() => {
    modalProps?.onSubmit(getValues()[PartnerDomainFormFields.Domain]);
    closeModalAndResetForm();
  }, [modalProps, closeModalAndResetForm, getValues]);

  const onClose = useCallback(() => {
    closeModalAndResetForm();
  }, [closeModalAndResetForm]);

  const validateDomain = useCallback(
    (data: string): boolean => {
      const isValidDomain = DOMAIN_VALIDATION_REGEX.test(data);

      if (!isValidDomain) {
        setError(PartnerDomainFormFields.Domain, {
          message: 'Should be a valid domain',
        });
      }

      const alreadyExistingDomain = (modalProps?.existingDomains ?? []).find(
        (item) => item === data,
      );

      if (alreadyExistingDomain) {
        setError(PartnerDomainFormFields.Domain, {
          message: 'Domain already added',
        });
      }

      const isValid = isValidDomain && !alreadyExistingDomain;

      if (isValid) {
        clearErrors(PartnerDomainFormFields.Domain);
      }

      return isValid;
    },
    [modalProps?.existingDomains, setError, clearErrors],
  );

  return (
    <Dialog open={isOpened} onClose={onClose} fullWidth>
      <DialogTitle>
        <Typography>Add a new allowed domain for sign up</Typography>
      </DialogTitle>
      <DialogContent>
        <Stack sx={{ marginTop: '1rem' }} flexDirection="column" alignItems="flex-end">
          <FormControl
            variant="standard"
            sx={{
              width: '100%',
            }}
          >
            <Controller
              control={control}
              name={PartnerDomainFormFields.Domain}
              rules={{ required: true }}
              render={({ field }) => (
                <TextField
                  sx={{
                    minWidth: '100%',
                  }}
                  label="Domain"
                  {...register(PartnerDomainFormFields.Domain, {
                    validate: validateDomain,
                  })}
                  error={!!formState.errors[PartnerDomainFormFields.Domain]}
                  helperText={
                    formState.errors[PartnerDomainFormFields.Domain]
                      ? formState.errors[PartnerDomainFormFields.Domain].message
                      : ''
                  }
                  {...field}
                />
              )}
            />
          </FormControl>

          <Button
            disabled={!formState.isValid}
            sx={{ alignSelf: 'flex-end', marginTop: theme.spacing(2) }}
            onClick={onFormSubmit}
          >
            Add
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
